/**
 * The shell's column geometry, for every module (G0; docs/audits/PILOT-QUOTES-SHELL.md §2.1-§2.2,
 * S1b and S5). Pure: no React, no DOM. ListRecordSplit (layout) and the tool host
 * (layout/tools) both read these numbers; neither restates them, and neither depends on a
 * module. These used to live in quotes/QuoteSplit.jsx, which still re-exports them.
 *
 * THE SPLIT. W is the width of the split's root (it spans <main>):
 *   list   = clamp(300 px, 30% of W, 440 px)
 *   record = W − list − 1 px divider (≥ 640)
 *   two columns when W ≥ 941 (300 + 1 + 640); below that the record covers the list.
 * With a tool column open beside <main>, W is what is left beside it and the list yields
 * first: it takes its width for the whole area (W + column), gives up the column's width down
 * to LIST_MIN, and the record takes the rest (§2.1's 1440 row: column 340 -> list 300).
 *
 * THE TOOL PANEL (push or sheet). The panel is a column beside <main> (it pushes the page)
 * when the area beside the rail keeps its minimum after the panel and the divider:
 *     W - width - DIVIDER >= SPLIT_MIN   with a record open (the split)
 *     W - width - DIVIDER >= RECORD_MIN  with the list alone
 * Otherwise it is the sheet: 360 px over the record, anchored right. Below RAIL_DEFAULT_BELOW
 * (the tablet) it is always the sheet. The column is 340 by default, else the user's
 * panes.toolPanelWidth, clamped 280-480, then capped by the room so a width never flips the
 * mode; if even 280 does not fit, the sheet. The sheet is 360, fixed, and never written.
 */
import { RAIL_DEFAULT_BELOW } from '../../utils/sidebarLayout';

// ── the split ─────────────────────────────────────────────────────────────────────────
export const LIST_MIN = 300;
export const LIST_MAX = 440;
export const LIST_SHARE = 0.3;
export const RECORD_MIN = 640;
export const DIVIDER = 1;
export const SPLIT_MIN = LIST_MIN + DIVIDER + RECORD_MIN; // 941
export const STEP_DEBOUNCE_MS = 150;
/**
 * Below this W a module's full list condenses (tablet pass 2026-10-20, items 5-6): at 768 the
 * mobile layout's W is 710, too narrow for Orders' eight columns or Customers' table. Orders
 * condenses its table; Customers shows its card view.
 */
export const CONDENSED_BELOW = 900;

/** The column widths for a W, or null when W is too narrow for two columns. */
export function splitGeometry(w, toolColumn = 0) {
  if (!Number.isFinite(w) || w < SPLIT_MIN) return null;
  const whole = w + (toolColumn > 0 ? toolColumn : 0);
  const share = Math.min(LIST_MAX, Math.max(LIST_MIN, Math.round(whole * LIST_SHARE)));
  const list = toolColumn > 0 ? Math.max(LIST_MIN, share - toolColumn) : share;
  return { list, record: w - list - DIVIDER };
}

// ── the tool panel ────────────────────────────────────────────────────────────────────
export const TOOL_COLUMN_DEFAULT = 340;
export const TOOL_COLUMN_MIN = 280;
export const TOOL_COLUMN_MAX = 480;
export const TOOL_SHEET_WIDTH = 360;
export const TOOL_RAIL_WIDTH = 44;
/** One keyboard step of the resize handle. */
export const TOOL_RESIZE_STEP = 16;

export const clampToolWidth = (w) => Math.min(TOOL_COLUMN_MAX, Math.max(TOOL_COLUMN_MIN, Math.round(w)));

/** The widest column the area can give: W minus the layout's minimum and the divider. */
export function toolColumnRoom(areaWidth, layout) {
  const need = layout === 'split' ? SPLIT_MIN : RECORD_MIN;
  return Math.floor(areaWidth - need - DIVIDER);
}

/**
 * @param {object} p
 * @param {number} p.areaWidth     W: the content area beside the rail (main plus the column)
 * @param {'split'|'single'} p.layout  a record open, or the list alone
 * @param {number} p.viewportWidth window.innerWidth
 * @param {number|null} p.storedWidth panes.toolPanelWidth
 * @returns {{ mode: 'column'|'sheet', width: number }}
 */
export function toolPanelLayout({ areaWidth, layout, viewportWidth, storedWidth }) {
  const sheet = { mode: 'sheet', width: TOOL_SHEET_WIDTH };
  if (!(viewportWidth >= RAIL_DEFAULT_BELOW)) return sheet;
  const wanted = clampToolWidth(Number.isFinite(storedWidth) ? storedWidth : TOOL_COLUMN_DEFAULT);
  const width = Math.min(wanted, toolColumnRoom(areaWidth, layout));
  return width >= TOOL_COLUMN_MIN ? { mode: 'column', width } : sheet;
}
